import { useState, useEffect } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, Phone, Navigation, Fuel, Search, AlertCircle } from "lucide-react";
import { Header } from "../components/header";
import { Footer } from "../components/footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { MapContainer } from "../components/map-container";

export default function MapPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStations, setFilteredStations] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    fetchStations();
  }, [navigate]);

  const fetchStations = async () => {
    try {
      const { data } = await api.get("/api/stations");
      console.log("[GeoGas] Estaciones cargadas:", data);
      setStations(data);
      setFilteredStations(data);
      setError("");
    } catch (err) {
      console.error("[GeoGas] Error:", err);
      setError("No se pudieron cargar las estaciones. Intenta de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = stations.filter(
      (station) =>
        station.name.toLowerCase().includes(query) ||
        station.address.toLowerCase().includes(query)
    );
    setFilteredStations(filtered);
  }, [searchQuery, stations]);

  const getStockColor = (stock) => {
    switch (stock) {
      case "available": return "bg-green-600 text-white";
      case "low": return "bg-yellow-500 text-black";
      case "out": return "bg-red-600 text-white";
      default: return "bg-gray-500 text-white";
    }
  };

  const getStockText = (stock) => {
    switch (stock) {
      case "available": return "Disponible";
      case "low": return "Stock Bajo";
      case "out": return "Agotado";
      default: return "Desconocido";
    }
  };

  const formatDistance = (meters) => {
    if (!meters) return null;
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0288d0]/5 to-white">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-[#0288d0]" />
          <p className="text-lg text-muted-foreground">Cargando estaciones de gas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 bg-gradient-to-b from-[#0288d0]/5 to-white">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="mb-6 text-center md:text-left">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0288d0] mb-2">
              Localiza Gas en Malabo
            </h1>
            <p className="text-muted-foreground text-base md:text-lg">
              Encuentra bombonas de gas cerca de ti con stock actualizado en tiempo real
            </p>
          </div>

          <div className="mb-6 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nombre, barrio o dirección..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
          </div>

          {error && (
            <div className="mb-6 max-w-2xl mx-auto">
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="overflow-hidden shadow-lg h-[500px] lg:h-full min-h-[500px]">
                <CardContent className="p-0 h-full">
                  <MapContainer 
                    stations={filteredStations} 
                    onStationSelect={setSelectedStation}
                    selectedStationId={selectedStation?.id}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Card className="shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#0288d0]">
                    <Fuel className="h-5 w-5" />
                    Estaciones de Gas
                  </CardTitle>
                  <CardDescription>
                    {filteredStations.length} {filteredStations.length === 1 ? "estación encontrada" : "estaciones encontradas"}
                  </CardDescription>
                </CardHeader>
              </Card>

              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {filteredStations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No se encontraron estaciones</p>
                    <p className="text-sm mt-1">Prueba con otro término de búsqueda</p>
                  </div>
                ) : (
                  filteredStations.map((station) => (
                    <Card
                      key={station.id}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 ${
                        selectedStation?.id === station.id 
                          ? "border-[#0288d0] ring-2 ring-[#0288d0]/20" 
                          : "border-border hover:border-[#0288d0]/50"
                      }`}
                      onClick={() => setSelectedStation(station)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-lg leading-tight">{station.name}</CardTitle>
                          <Badge className={getStockColor(station.stock)}>
                            {getStockText(station.stock)}
                          </Badge>
                        </div>
                        {station.distance && (
                          <p className="text-xs text-muted-foreground mt-1">
                            A {formatDistance(station.distance)} de ti
                          </p>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{station.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <a 
                            href={`tel:${station.phone}`} 
                            className="text-[#0288d0] hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {station.phone}
                          </a>
                        </div>

                        {(station.stock_espanol !== undefined || station.stock_francesa !== undefined) && (
                          <div className="text-sm pt-2 border-t border-border/50">
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Española (12.5kg):</span>
                              <span className="font-semibold text-green-700">
                                {station.stock_espanol || 0} uds
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Francesa (13kg):</span>
                              <span className="font-semibold text-blue-700">
                                {station.stock_francesa || 0} uds
                              </span>
                            </div>
                          </div>
                        )}

                        <Button
                          size="sm"
                          variant="default"
                          className="w-full mt-3 bg-[#0288d0] hover:bg-[#0288d0]/90 text-white font-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `https://www.google.com/maps/dir/?api=1&destination=${station.latitude},${station.longitude}&travelmode=driving`,
                              "_blank"
                            );
                          }}
                        >
                          <Navigation className="h-4 w-4 mr-2" />
                          Ir con Google Maps
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}